import { createContext, Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react'

import { getCurrentUserConsultation } from '@/api/consultations'
import { useJourney } from '@/hooks/useJourney'
import { useLoading } from '@/hooks/useLoading'
import { useModule } from '@/hooks/useModule'
import { useStep } from '@/hooks/useStep'
import { useTraces } from '@/hooks/useTraces'
import { useUser } from '@/hooks/useUser'
import {
  Consultation,
  CONSULTATION_LANDING_STEP,
  CONSULTATION_STATUS,
  CONSULTATION_STEPS,
  CONSULTATION_TYPE,
  Question,
  STEP_STEPPER
} from '@/types/consultation'
import { ConsultationModule } from '@/types/module'
import { STEP_TYPE } from '@/types/step'
import { CASE_STATUS_STEP } from '@/types/websocket'

export type Attachment = {
  file: File
  preview: string | ArrayBuffer | null
  url: string
}

export interface ConsultationContextProps {
  currentConsultationStep: CONSULTATION_STEPS | null
  setCurrentConsultationStep: Dispatch<SetStateAction<CONSULTATION_STEPS | null>>
  consultation: Consultation | null
  setConsultation: Dispatch<SetStateAction<Consultation | null>>
  currentQuestion: Question
  setCurrentQuestion: Dispatch<SetStateAction<Question>>
  attachments: Attachment[]
  setAttachments: Dispatch<SetStateAction<Attachment[]>>
  consultationType: CONSULTATION_TYPE
  setConsultationType: Dispatch<SetStateAction<CONSULTATION_TYPE>>
}

export const ConsultationContext = createContext({} as ConsultationContextProps)

interface ConsultationProviderProps {
  children: ReactNode
}

export const ConsultationProvider = ({ children }: ConsultationProviderProps) => {
  const { updateStep } = useStep()
  const { addTrace } = useTraces()
  const { user } = useUser()
  const { initialized } = useJourney()
  const { showLoading, hideLoading } = useLoading()
  const { currentStep } = useStep()
  const { currentModule } = useModule()

  const [currentConsultationStep, setCurrentConsultationStep] = useState<ConsultationContextProps['currentConsultationStep']>(null)
  const [consultation, setConsultation] = useState<ConsultationContextProps['consultation']>(null)
  const [currentQuestion, setCurrentQuestion] = useState<ConsultationContextProps['currentQuestion']>({
    question: '',
    attachments: []
  })
  const [consultationType, setConsultationType] = useState<CONSULTATION_TYPE>(CONSULTATION_TYPE.MEDICAL)

  const [attachments, setAttachments] = useState<Attachment[]>([])

  useEffect(() => {
    setCurrentQuestion((prev) => ({
      ...prev,
      attachments: attachments.map((attachment) => attachment.url)
    }))
  }, [attachments])

  useEffect(() => {
    if (!currentConsultationStep || currentConsultationStep === CONSULTATION_STEPS.FORM) return

    updateStep({
      id: `consultation-${currentConsultationStep}`,
      name: `consultation-${currentConsultationStep}`,
      type: STEP_TYPE.CONSULTATION,
      components: {},
      componentsOrder: [],
      stepper: STEP_STEPPER[currentConsultationStep],
      backButton: currentConsultationStep === CONSULTATION_STEPS.LANDING ? (currentModule as ConsultationModule)?.backButton : undefined
    })
    if (currentConsultationStep === CONSULTATION_STEPS.LANDING) addTrace('module-consultation', `consultation-${currentConsultationStep}`)
  }, [currentConsultationStep])

  useEffect(() => {
    if (!initialized || !user?.data?.uniqueId) return

    if (!user?.data?.userId) return console.warn('No created user found')

    if (consultation) return

    showLoading()

    getCurrentUserConsultation({
      uniqueId: user.data.uniqueId
    })
      .then((currentConsultation) => {
        if (currentConsultation?.status === CONSULTATION_STATUS.CLOSED) return
        setConsultation(currentConsultation)
      })
      .catch(() => {
        console.warn('No consultation found')
        setConsultation(null)
      })
      .finally(() => {
        hideLoading()
      })
  }, [initialized])

  useEffect(() => {
    if (!consultation) return setCurrentConsultationStep(CONSULTATION_STEPS.LANDING)

    setCurrentConsultationStep(CASE_STATUS_STEP[consultation.status] || CONSULTATION_STEPS.LANDING)
  }, [consultation])

  useEffect(() => {
    // This is needed to update the current step when the user navigates back to the consultation landing step
    if (currentStep?.id === CONSULTATION_LANDING_STEP.id && currentConsultationStep !== CONSULTATION_STEPS.LANDING) {
      setCurrentConsultationStep(CONSULTATION_STEPS.LANDING)
    }
  }, [currentStep])

  const value = {
    currentConsultationStep,
    setCurrentConsultationStep,
    consultation,
    setConsultation,
    currentQuestion,
    setCurrentQuestion,
    attachments,
    setAttachments,
    consultationType,
    setConsultationType
  }

  return <ConsultationContext.Provider value={value}>{children}</ConsultationContext.Provider>
}
